import { Button, IconButton, Menu, MenuItem, MenuSub } from '@hive/component';
import { usePageHelper } from '@hive/core/blocksuite/block-suite-page-list/utils';
import { useAsyncCallback } from '@hive/core/components/hooks/hive-async-hooks';
import { useRequireSubscription } from '@hive/core/components/hooks/use-require-subscription';
import { DocsService } from '@hive/core/modules/doc';
import { EditorSettingService } from '@hive/core/modules/editor-setting';
import { TemplateDocService } from '@hive/core/modules/template-doc';
import { TemplateListMenuContentScrollable } from '@hive/core/modules/template-doc/view/template-list-menu';
import { WorkbenchService } from '@hive/core/modules/workbench';
import { WorkspaceService } from '@hive/core/modules/workspace';
import { inferOpenMode } from '@hive/core/utils';
import { useI18n } from '@hive/i18n';
import track from '@hive/track';
import type { DocMode } from '@blocksuite/hive/model';
import {
  ArrowDownSmallIcon,
  EdgelessIcon,
  PageIcon,
  PlusIcon,
  TemplateIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import type React from 'react';
import { type MouseEvent, useCallback } from 'react';

import * as styles from './index.css';

/**
 * @return a function to create a new doc, will duplicate the template doc if the page template is enabled
 */
const useNewDoc = () => {
  const workspaceService = useService(WorkspaceService);
  const templateDocService = useService(TemplateDocService);
  const docsService = useService(DocsService);
  const workbench = useService(WorkbenchService).workbench;

  const currentWorkspace = workspaceService.workspace;
  const enablePageTemplate = useLiveData(
    templateDocService.setting.enablePageTemplate$
  );
  const pageTemplateDocId = useLiveData(
    templateDocService.setting.pageTemplateDocId$
  );

  const pageHelper = usePageHelper(currentWorkspace.docCollection);

  const createPage = useAsyncCallback(
    async (e?: MouseEvent, mode?: DocMode) => {
      if (enablePageTemplate && pageTemplateDocId) {
        const docId =
          await docsService.duplicateFromTemplate(pageTemplateDocId);
        workbench.openDoc(docId, { at: inferOpenMode(e) });
      } else {
        pageHelper.createPage(mode, { at: inferOpenMode(e) });
      }
    },
    [docsService, enablePageTemplate, pageHelper, pageTemplateDocId, workbench]
  );

  return createPage;
};

interface AddPageButtonProps {
  className?: string;
  style?: React.CSSProperties;
}

const sideBottom = { side: 'bottom' as const };
export function AddPageButton(props: AddPageButtonProps) {
  const editorSetting = useService(EditorSettingService);
  const newDocDefaultMode = useLiveData(
    editorSetting.editorSetting.settings$.selector(s => s.newDocDefaultMode)
  );

  return newDocDefaultMode === 'ask' ? (
    <AddPageWithAsk {...props} />
  ) : (
    <AddPageWithoutAsk {...props} />
  );
}

function AddPageWithAsk({ className, style }: AddPageButtonProps) {
  const t = useI18n();
  const createDoc = useNewDoc();
  const workbench = useService(WorkbenchService).workbench;
  const docsService = useService(DocsService);
  const { isFreeUser, showUpgradeModal } = useRequireSubscription();

  const createPage = useCallback(
    (e?: MouseEvent) => {
      if (isFreeUser) {
        showUpgradeModal();
        return;
      }
      createDoc(e, 'page');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'page' });
    },
    [createDoc, isFreeUser, showUpgradeModal]
  );
  const createEdgeless = useCallback(
    (e?: MouseEvent) => {
      if (isFreeUser) {
        showUpgradeModal();
        return;
      }
      createDoc(e, 'edgeless');
      track.$.navigationPanel.$.createDoc();
      track.$.sidebar.newDoc.quickStart({ with: 'edgeless' });
    },
    [createDoc, isFreeUser, showUpgradeModal]
  );

  const createDocFromTemplate = useAsyncCallback(
    async (templateId: string) => {
      if (isFreeUser) {
        showUpgradeModal();
        return;
      }
      const docId = await docsService.duplicateFromTemplate(templateId);
      workbench.openDoc(docId);
      track.$.sidebar.newDoc.quickStart({ with: 'template' });
    },
    [docsService, workbench, isFreeUser, showUpgradeModal]
  );

  return (
    <Menu
      items={
        <>
          <MenuItem
            prefixIcon={<PageIcon />}
            onClick={createPage}
            onAuxClick={createPage}
          >
            {t['Page']()}
          </MenuItem>
          <MenuItem
            prefixIcon={<EdgelessIcon />}
            onClick={createEdgeless}
            onAuxClick={createEdgeless}
          >
            {t['Edgeless']()}
          </MenuItem>
          <MenuSub
            triggerOptions={{
              prefixIcon: <TemplateIcon />,
            }}
            subContentOptions={{
              sideOffset: 16,
              className: styles.templateMenu,
            }}
            items={
              <TemplateListMenuContentScrollable
                onSelect={createDocFromTemplate}
              />
            }
          >
            {t['Template']()}
          </MenuSub>
        </>
      }
    >
      <Button
        tooltip={t['New Page']()}
        tooltipOptions={sideBottom}
        data-testid="sidebar-new-page-with-ask-button"
        className={clsx([styles.withAskRoot, className])}
        style={style}
      >
        <div className={styles.withAskContent}>
          <PlusIcon />
          <ArrowDownSmallIcon />
        </div>
      </Button>
    </Menu>
  );
}

function AddPageWithoutAsk({ className, style }: AddPageButtonProps) {
  const createDoc = useNewDoc();
  const { isFreeUser, showUpgradeModal } = useRequireSubscription();

  const onClickNewPage = useCallback(
    (e?: MouseEvent) => {
      if (isFreeUser) {
        showUpgradeModal();
        return;
      }
      createDoc(e);
      track.$.navigationPanel.$.createDoc();
    },
    [createDoc, isFreeUser, showUpgradeModal]
  );

  const t = useI18n();

  return (
    <IconButton
      tooltip={t['New Page']()}
      tooltipOptions={sideBottom}
      data-testid="sidebar-new-page-button"
      style={style}
      className={clsx([styles.root, className])}
      onClick={onClickNewPage}
      onAuxClick={onClickNewPage}
    >
      <PlusIcon />
    </IconButton>
  );
}
